// The messages between the game (the host), the llamas' browsers, a duel's
// rival (a second game) and the room. Everything is JSON text. Times are the
// room's clock, in ms since the epoch.

// 3 (2026-10-23): the rival role and `duel`. The party activities had grown
// protocol 2 without a bump, so an old room swallowed them in silence.
export const PROTOCOL_VERSION = 3;

export const SEAT_COUNT = 99;
export const GUESS_MAX = 9999;
export const LLAMA_MAX = 1000;
// A llama's public number: four digits, unique in its room, shown as LL#1234.
export const LLAMA_ID_LEAST = 1000;
export const LLAMA_ID_MOST = 9999;
// A Turnstile proof can be 2048 characters, so a frame can be up to 4 KB.
export const MESSAGE_MAX_BYTES = 4096;

// Presence. A llama's page pings while it is on screen, and the room answers
// with no wake (the hibernation API's auto-response). A seat with no ping in
// PRESENCE_STALE_MS is away: the room does not wait on it, and the game
// shows it asleep. A page that goes to the background says `away` at once;
// a phone that sleeps or a browser that dies just stops pinging.
export const PING_TEXT = '{"t":"ping"}';
export const PONG_TEXT = '{"t":"pong"}';
export const PING_EVERY_MS = 10_000;
export const PRESENCE_STALE_MS = 25_000;
export const PROOF_MAX_LENGTH = 2048;

export const DELAY_DEFAULT_MS = 6000;
export const DELAY_MAX_MS = 30000;
export const GRACE_MS = 8000;
export const ROOM_IDLE_EXPIRE_MS = 2 * 60 * 60 * 1000;

// A llama's colour is an index into the game's rainbow (`ColorPalette.LLAMA_HUES`
// and `HUES` in src/web/hues.ts). A joining llama takes one of the least-used.
export const HUE_COUNT = 10;

// A llama rates its employer once a minute. The room's mood is the mean of the
// votes cast in the last ten minutes, one vote per llama.
export const MOOD_COOLDOWN_MS = 60_000;
export const MOOD_WINDOW_MS = 10 * 60_000;
export const MOODS = [-1, 0, 1] as const;
export type Mood = (typeof MOODS)[number];

export const EMOTES = ['heart', 'spit', 'what', 'ping', 'zzz'] as const;
export type Emote = (typeof EMOTES)[number];
export const EMOTE_COOLDOWN_MS = 1500;

// The leaderboard every llama sees holds this many rows.
export const BOARD_TOP = 50;

// A llama's name, as the phone asked for it. Printable ASCII, this long at
// most, so it fits a plank in the game's bitmap fonts. Empty means none, and
// the game then names the llama by its number.
export const NAME_LENGTH_MOST = 8;

// A party activity's kind and phase are short tokens the game chooses.
export const ACTIVITY_WORD_PATTERN = /^[a-z][a-z0-9_]{0,23}$/;

export const ROOM_CODE_LETTERS = 'BCDFGHJKMNPQRSTVWXZ';
export const ROOM_CODE_LENGTH = 4;
export const ROOM_CODE_TRIES = 8;

export type Phase = 'counting' | 'closed' | 'done';

// How a run ended: `over` at the game over, `quit` when the Countant walked
// off the job to the title.
export const RUN_OUTCOMES = ['over', 'quit'] as const;
export type RunOutcome = (typeof RUN_OUTCOMES)[number];
export type RunPhase = 'playing' | 'over';

// One llama's numbers for the run: stages guessed, guesses right on the
// count, and tips earned.
export interface RunLine {
  stages: number;
  right: number;
  earned: number;
}

// `id` changes when a seat changes hands, so the game can tell a new llama.
// `name` is empty when the llama gave none. `online` is false while the
// llama's socket is down: it keeps its seat, and the game can show it dark.
export interface SeatView {
  seat: number;
  id: number;
  hue: number;
  name: string;
  online: boolean;
}

// A party activity: what the phones show and play right now. The game owns
// the rules and the ids. The room keeps the latest and hands it to a phone
// that says hello, so a late or returning phone lands on the current screen.
// `at` is the room's clock when the game sent it. `data` is the game's own
// business, inside the message cap.
export interface Activity {
  id: number;
  kind: string;
  phase: string;
  at: number;
  data: unknown;
}

// One leaderboard row. `id` is the llama's public number, never its token.
export interface BoardRow {
  id: number;
  hue: number;
  earned: number;
  demerits: number;
  stages: number;
  streak: number;
}

export interface Standing {
  rank: number;
  earned: number;
  demerits: number;
  stages: number;
  streak: number;
}

export interface GuessView {
  seat: number;
  value: number;
}

// The game sends these. Every one but hello needs the host token checked first.
export type HostMsg =
  | { t: 'hello'; role: 'host'; token: string; protocol: number }
  | { t: 'config'; delayMs: number }
  | { t: 'open'; stage: number }
  | { t: 'close'; stage: number }
  | { t: 'result'; stage: number; actual: number }
  | { t: 'pause' }
  | { t: 'resume' }
  | { t: 'kick'; seat: number }
  | { t: 'tip'; amount: number }
  | { t: 'lock'; locked: boolean }
  | { t: 'clear_queue' }
  // `party`: a party's run. A llama that misses a stage keeps its seat.
  | { t: 'run_start'; party?: boolean }
  | { t: 'run_end'; outcome: RunOutcome; stage: number }
  // A party activity begins, or moves to a new phase. The room keeps it.
  | { t: 'activity'; id: number; kind: string; phase: string; data: unknown }
  // The activity is over. The phones go back to the hold.
  | { t: 'activity_end' }
  // A word to the duel's rival. The room relays it untouched and keeps nothing.
  | { t: 'duel'; data: unknown };

// A second game, playing the same run on its own screen: the duel's rival.
// It joins with the room's code and a token of its own, takes no seat, and
// only speaks to the host.
export type RivalMsg =
  | { t: 'hello'; role: 'rival'; token: string; protocol: number; name?: string }
  | { t: 'duel'; data: unknown };

export type LlamaMsg =
  | { t: 'hello'; role: 'llama'; token: string; protocol: number; name?: string }
  | { t: 'guess'; stage: number; value: number }
  // A move in the current activity. The room relays it to the host with the
  // seat, and drops it when `id` is not the activity that is on.
  | { t: 'play'; id: number; data: unknown }
  // The counter as it stands. At the deadline a tally above zero counts as
  // the guess of a llama that did not lock one in.
  | { t: 'tally'; stage: number; value: number }
  | { t: 'mood'; value: Mood }
  | { t: 'emote'; kind: Emote }
  | { t: 'leave' }
  // The page went to the background, or came back to the front.
  | { t: 'away' }
  | { t: 'here' }
  // The answer to a `challenge`: the Turnstile token from the widget.
  | { t: 'proof'; token: string };

export type ClientMsg = HostMsg | LlamaMsg | RivalMsg;

export type ToHost =
  | { t: 'welcome'; now: number; delayMs: number }
  | { t: 'roster'; seats: SeatView[]; queued: number; locked: boolean }
  | { t: 'guess'; stage: number; seat: number; value: number }
  | { t: 'deadline'; stage: number; at: number }
  | { t: 'done'; stage: number; guesses: GuessView[] }
  | { t: 'emote'; seat: number; kind: Emote }
  | { t: 'play'; id: number; seat: number; data: unknown }
  // The duel's rival came or went. `name` is what it called itself.
  | { t: 'rival'; online: boolean; name: string }
  // A word from the rival, untouched.
  | { t: 'duel'; data: unknown };

export type ToRival =
  | { t: 'welcome'; now: number; delayMs: number }
  | { t: 'host'; online: boolean }
  | { t: 'duel'; data: unknown }
  // A rival is on already.
  | { t: 'full' };

export type ToLlama =
  | { t: 'welcome'; now: number; delayMs: number }
  | { t: 'you'; seat: number | null; place: number | null; id: number; hue: number; name: string }
  // The party activity that is on, or null when none is.
  | { t: 'activity'; activity: Activity | null }
  | { t: 'stage'; stage: number; phase: Phase; at: number; deadline: number | null }
  | { t: 'locked'; stage: number; value: number }
  | { t: 'booted'; stage: number }
  | { t: 'result'; stage: number; actual: number; guess: number; demerit: number }
  | { t: 'paused'; paused: boolean }
  | { t: 'host'; online: boolean }
  | { t: 'config'; delayMs: number }
  | { t: 'mood'; mood: number | null; votes: number }
  | { t: 'voted'; value: Mood; nextAt: number }
  | { t: 'board'; rows: BoardRow[] }
  | { t: 'standing'; standing: Standing }
  | { t: 'tipped'; stage: number; amount: number; share: number }
  // The run started or ended. `mine` is this llama's line for the run.
  | {
      t: 'run';
      n: number;
      phase: RunPhase;
      // Room time when the run started or ended.
      at: number;
      outcome: RunOutcome | null;
      stage: number | null;
      mine: RunLine;
    }
  | { t: 'kicked' }
  | { t: 'full' }
  // Pass Turnstile before the room lets this llama in.
  | { t: 'challenge'; siteKey: string }
  // The host locked the room, and this llama has no seat or place.
  | { t: 'room_locked' }
  // The host cleared the queue, and this llama lost its place.
  | { t: 'cleared' };

// `closed`: Llama Link is shut for now (the `closed` mode). The socket closes next.
export type ToAny = { t: 'old' } | { t: 'gone' } | { t: 'refused'; why: string } | { t: 'closed' };

export function makeRoomCode(random: () => number): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += ROOM_CODE_LETTERS[Math.floor(random() * ROOM_CODE_LETTERS.length)];
  }
  return code;
}

export function parseRoomCode(text: string): string | null {
  const code = text.trim().toUpperCase();
  if (code.length !== ROOM_CODE_LENGTH) return null;
  for (const letter of code) {
    if (!ROOM_CODE_LETTERS.includes(letter)) return null;
  }
  return code;
}

export const llamaTag = (id: number): string => `LL#${id}`;

// A name as the room keeps it: printable ASCII, single spaces, trimmed, cut
// to the cap. Anything else answers the empty name.
export function cleanName(text: unknown): string {
  if (typeof text !== 'string') return '';
  return text
    .replace(/[^\x20-\x7e]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, NAME_LENGTH_MOST)
    .trim();
}

export const isActivityWord = (value: unknown): value is string =>
  typeof value === 'string' && ACTIVITY_WORD_PATTERN.test(value);

export function isGuessValue(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= GUESS_MAX;
}

export function isMood(value: unknown): value is Mood {
  return MOODS.includes(value as Mood);
}

export function isRunOutcome(value: unknown): value is RunOutcome {
  return RUN_OUTCOMES.includes(value as RunOutcome);
}

export function isEmote(value: unknown): value is Emote {
  return EMOTES.includes(value as Emote);
}
